import Modal from '../Modal';
import { useModal } from '../../../providers/ModalProvider';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import { Text } from '../../typography';
import UserMultiSelector from '../../UserMultiSelector';
import styles from './CreateAssessmentModal.module.scss';
import { useInspection } from '../../../hooks/offline-hooks/createInspectionhook';
import { useNavigate } from 'react-router-dom';
import { getRoute, specialTemplateTypes } from '../../../constants/strings';
import { paths } from '../../../constants/strings';
import { useEffect, useRef, useState, useMemo } from 'react';
import { modals } from '../../../providers/modals';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import { Checkbox } from '@mui/material';
import { useChangeWorkspace } from '../../../hooks/misc';
import { useTemplates } from '../../../providers/TemplateProvider';
import { useCurrentUser } from '../../../providers/UserProvider';
import { Form } from 'react-bootstrap';
import { customColors, vehicleString } from '../../../utilities';
import { useIsMobile } from '../../../hooks/misc';
import Icon from '../../Icon';
import InspectionTypeBadge from '../../inspections/InspectionTypeBadge';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useOnlineStatus } from '../../../hooks/offline-hooks/offline-misc';
import Spacer from '../../typography/Spacer';
import Dropdown from '../../dropdownV2/Dropdown';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';
import { getLocalTime } from '../../../utilities/time';
import { showToast } from '../../../graphql/cache/modal';
import UniversalDropdown from '../../dropdownV2/UniversalDropdown';
import Button from '../../Button';

const lastAssessmentQuery = loader(
  './CreateAssessmentModal.lastAssessment.graphql',
);

export default function CreateAssessmentModal() {
  const online = useOnlineStatus();
  const { modalState, closeModal, updateModal, openModal } = useModal();
  const { t } = useTranslation();
  const {
    workspaceId,
    workspaceVehicles,
    workspace,
    allVehicles,
    availableWorkspaces,
    workspaceLocations,
  } = useWorkspace();
  const { changeWorkspace } = useChangeWorkspace();
  const { templates, activeTemplates } = useTemplates();
  const {
    user: currentUser,
    assessmentTypes,
    activeTemplateTypes,
    companyLocations,
  } = useCurrentUser();
  const isMilitary = currentUser?.timeSetting === 'military';
  const isMobile = useIsMobile();
  const { addInspection } = useInspection();
  const navigate = useNavigate();
  const assetInputRef = useRef();
  const [selectedWorkspace, setSelectedWorkspace] = useState(workspace);
  const [editing, setEditing] = useState('TYPE');
  const { simpleAssessmentModal } = useFlags();
  const [target, setTarget] = useState(null);

  const {
    show,
    modalTitle,
    types = [],
    timecardId,
    incidentId,
    asset,
    participants,
    template,
    type,
    dataWorkspaceId,
    private: privateAssessment,
    allWorkspaces,
    subject,
    conditionalMetadata,
    location,
    anonymous,
    riskRegistryId,
  } = modalState.createAssessmentModal;

  const update = (variables) =>
    updateModal({
      modalName: modals.createAssessment,
      variables: { ...variables },
    });

  const onHide = (id) => {
    setSelectedWorkspace(workspace);
    const navigateTo = id
      ? getRoute(
          dataWorkspaceId || selectedWorkspace?.id || workspaceId,
          paths.assessment,
          id,
        )
      : undefined;
    closeModal({ modalName: modals.createAssessment, navigateTo });
  };

  const submitDisabled = useMemo(() => {
    const requireLocation = !!template?.templateOptions?.location;
    const requireSubject = !!template?.templateOptions?.subject;
    const requireAsset = type?.key === 'VEHICLE' || !!template?.assetRequired;

    return (
      !template?.id ||
      !type ||
      (requireAsset && !asset?.id) ||
      (requireLocation && !location) ||
      (requireSubject && !subject)
    );
  }, [
    template?.templateOptions?.location,
    template?.templateOptions?.subject,
    template?.assetRequired,
    template?.id,
    asset?.id,
    location,
    subject,
    type,
  ]);

  const allowAnonymous = useMemo(() => {
    return template?.templateOptions?.anonymous === true;
  }, [template?.templateOptions?.anonymous]);

  const optional = useMemo(() => {
    if (template?.assetRequired === true) {
      return false;
    }
    if (!!type && type?.key !== 'VEHICLE') {
      return true;
    }

    return false;
  }, [template, type]);

  const assetsForSelection = useMemo(() => {
    if (!!template?.id && !!template?.vehicles?.length) {
      const vehicleIds = template?.vehicles?.map((v) => v.id) || [];
      const vehicles = allVehicles?.filter((v) => vehicleIds?.includes(v.id));
      return vehicles;
    }
    return allWorkspaces ? allVehicles : workspaceVehicles;
  }, [
    allVehicles,
    allWorkspaces,
    template?.id,
    template?.vehicles,
    workspaceVehicles,
  ]);

  const assetTemplates = useMemo(() => {
    const showAllTypes = type?.key === 'ALL_TYPES';
    const templatesList = showAllTypes
      ? activeTemplates?.filter(
          (t) => !specialTemplateTypes.includes(t?.templateType),
        )
      : templates;
    if (!!asset?.id && asset?.templates?.length > 0) {
      const hasAssetTemplates =
        asset.templates
          ?.filter((t) =>
            showAllTypes
              ? !t.isDraft
              : t?.templateType === type?.key && !t.isDraft,
          )
          .map(({ id }) => templatesList?.find((t) => t?.id === id))
          ?.filter(Boolean)?.length > 0;

      return hasAssetTemplates
        ? asset.templates
            ?.filter((t) =>
              showAllTypes
                ? !t.isDraft
                : t?.templateType === type?.key && !t.isDraft,
            )
            .map(({ id }) => templatesList?.find((t) => t?.id === id))
            .filter(Boolean)
        : templatesList;
    } else {
      return templatesList;
    }
  }, [asset?.id, asset?.templates, templates, type?.key, activeTemplates]);

  const allActiveTemplates = Object.values(assessmentTypes)?.map((a, index) => {
    return { ...a, id: index };
  });

  const availableTypes = assetTemplates?.map((a) => a?.templateType);

  const assetTemplateTypes = useMemo(
    () =>
      Object.values(allActiveTemplates)?.filter((a) =>
        availableTypes?.includes(a?.key),
      ) || [],
    [availableTypes, allActiveTemplates],
  );

  const typeOptions = useMemo(
    () => [
      {
        key: 'ALL_TYPES',
        icon: 'article',
        title: keys.common.ALL_TYPES,
        color: 'green',
        active: true,
        editable: false,
      },
      ...(!!asset?.templates?.length
        ? assetTemplateTypes
        : types?.length
        ? types
        : Object.values(activeTemplateTypes).filter(
            (t) =>
              t.key !== 'AUDIT' &&
              t.key !== 'OBSERVATION' &&
              t.key !== 'INCIDENT',
          )),
    ],
    [activeTemplateTypes, types, assetTemplateTypes, asset],
  );

  const { data: { inspections: [lastAssessment] = [{}] } = {} } = useQuery(
    lastAssessmentQuery,
    {
      skip: !asset?.id || !template?.id || !online,
      variables: { assetId: `${asset?.id}`, templateId: `${template?.id}` },
    },
  );

  useEffect(() => {
    if (
      !!dataWorkspaceId &&
      workspaceId !== dataWorkspaceId &&
      show &&
      !isNaN(dataWorkspaceId)
    ) {
      changeWorkspace(dataWorkspaceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataWorkspaceId, show, workspaceId]);

  useEffect(() => {
    if (!!template?.id && template?.private === true) {
      update({ privateAssessment: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  useEffect(() => {
    if (isMobile && target === 'asset-input' && assetInputRef.current) {
      assetInputRef.current.scrollIntoView({ behaviour: 'smooth' });
      setTarget(null);
    }
  }, [target, isMobile]);

  const locations = useMemo(() => {
    return !!allWorkspaces ? companyLocations : workspaceLocations;
  }, [allWorkspaces, companyLocations, workspaceLocations]);

  return (
    <Modal
      open={show || false}
      onClose={onHide}
      title={modalTitle || t(keys.assessments.START_ASSESSMENT)}
      submitText={t(keys.action.START)}
      disableCloseOnSubmit
      hideCancel
      submitDisabled={
        submitDisabled || (template?.templateOptions?.location && !location)
      }
      onSubmit={() => {
        const isPrivate =
          type?.key === 'INCIDENT' || template?.private === true;
        addInspection({
          templateId: template?.id,
          vehicleId: asset?.id,
          inspectionType: template?.templateType,
          participants: participants?.map((user) => user.id) || [],
          workspaceId:
            allWorkspaces || type?.key === 'INCIDENT'
              ? null
              : dataWorkspaceId || selectedWorkspace?.id || workspaceId,
          incidentId,
          timecardId,
          privateAssessment: isPrivate || privateAssessment,
          subject,
          conditionalMetadata,
          location,
          anonymous,
          riskRegistryId,
        }).then((res) => {
          const id = res?.data?.addInspection?.id;
          const err = res?.errors;
          if (err?.length) {
            showToast({
              variant: 'warning',
              title: 'Error',
              message: `There was a problem starting the assessment. Error: ${err[0].message}`,
            });
          }
          if (!id) {
            return;
          }
          onHide(id);
        });
      }}
      footer={
        !isMobile && (
          <Button
            disabled={!template}
            outlined
            icon="qr_code_2"
            size="sm"
            value="QR Code"
            onClick={() => {
              const url = new URL(window.location.origin);
              url.searchParams.set('modalName', 'createAssessmentModal');
              url.searchParams.set('type', template?.templateType);
              url.searchParams.set('template', template?.id);
              url.searchParams.set('asset', asset?.id);
              openModal({
                modalName: modals.qr,
                variables: { url: url.toString() },
              });
            }}
          />
        )
      }
    >
      <div className={styles.modalContent}>
        <div className={styles.top}>
          <Text noMargin weight="semiBold" className={styles.title}>
            {t(keys.templates.TEMPLATE_TYPE)}
            <span className={styles.red}>*</span>
          </Text>
          {!simpleAssessmentModal &&
            (editing !== 'TYPE' ? (
              <div>
                <div className={styles.editable}>
                  <InspectionTypeBadge type={type?.key} weight="regular" />
                  {types?.length > 1 ? (
                    <Icon
                      style={{
                        fontSize: '1.2rem',
                        opacity: 0.8,
                        color: customColors.SECONDARY,
                      }}
                      onClick={() => {
                        setEditing('TYPE');
                        update({ type: null, template: null, asset: null });
                      }}
                    >
                      edit
                    </Icon>
                  ) : null}
                </div>
              </div>
            ) : (
              <UniversalDropdown
                onChange={(newType) => {
                  update({ type: newType, template: null });
                  setEditing(null);
                }}
                selected={type}
                options={typeOptions}
                type="templateType"
                isRequired={true}
                isClearable={false}
              />
            ))}
          <Spacer size="xs" />
          <div
            ref={assetInputRef}
            onClick={(e) => {
              e.stopPropagation();
              setTarget('asset-input');
            }}
          >
            <Text weight="semiBold" noMargin className={styles.title}>
              {t(keys.common.ASSET)}
              {optional ? (
                <span className={styles.secondary}>{` (${t(
                  keys.common.OPTIONAL,
                )})`}</span>
              ) : (
                <span className={styles.red}>*</span>
              )}
            </Text>
            {!!asset && editing !== 'ASSET' ? (
              <div className={styles.editable}>
                <Text noMargin size="md">
                  {vehicleString(asset)}
                </Text>
                <Icon
                  style={{
                    fontSize: '1.2rem',
                    opacity: 0.8,
                    color: customColors.SECONDARY,
                  }}
                  onClick={() => {
                    setEditing('ASSET');
                    update({ asset: null });
                  }}
                >
                  edit
                </Icon>
              </div>
            ) : (
              <Dropdown
                options={assetsForSelection}
                selected={asset}
                onChange={(newAsset) => {
                  update({ asset: newAsset });
                  if (newAsset?.templates?.length > 0) {
                    const templateToAdd = templates?.find(
                      (t) =>
                        t?.id ===
                        newAsset?.templates?.find(
                          (at) =>
                            !at?.isDraft && at?.templateType === type?.key,
                        )?.id,
                    );
                    if (
                      templateToAdd?.workspaceId === workspace?.id ||
                      templateToAdd?.workspaceId === null
                    ) {
                      update({ template: templateToAdd });
                    }
                  }
                  setEditing(null);
                }}
                type="asset"
                isRequired={!optional}
              />
            )}
          </div>
          <Spacer size="xs" />
          <div>
            <Text weight="semiBold" noMargin className={styles.title}>
              {t(keys.common.TEMPLATE)}
              <span className={styles.red}>*</span>
            </Text>
            {!!template && editing !== 'TEMPLATE' ? (
              <div className={styles.editable}>
                <Text noMargin size="md">
                  {template?.title}
                </Text>
                <Icon
                  style={{
                    fontSize: '1.2rem',
                    opacity: 0.8,
                    color: customColors.SECONDARY,
                  }}
                  onClick={() => {
                    setEditing('TEMPLATE');
                    update({ template: null });
                  }}
                >
                  edit
                </Icon>
              </div>
            ) : (
              <UniversalDropdown
                type="template"
                options={[...(assetTemplates || [])].sort((a, b) =>
                  a?.title?.localeCompare(b?.title),
                )}
                onChange={(newTemplate) => {
                  update({ template: newTemplate });
                  setEditing(null);
                }}
                selected={template}
                isRequired
                placeholder={`Select Template...`}
              />
            )}
          </div>
        </div>
        <Spacer size="xs" />

        {!simpleAssessmentModal && (
          <div className={styles.bottom}>
            <Text weight="semiBold" noMargin size="sm-md">
              {t(keys.common.WORKSPACE)}
              <span className={styles.red}>*</span>
            </Text>
            <Dropdown
              isClearable={false}
              selected={selectedWorkspace}
              type="workspace"
              options={availableWorkspaces}
              onChange={(workspace) => {
                update({
                  allWorkspaces: !workspace?.id,
                  participants: [currentUser],
                });
                setSelectedWorkspace(workspace);
                if (!!workspace?.id) {
                  changeWorkspace(workspace.id);
                }
              }}
            />
            <Spacer />
            <Text
              weight="semiBold"
              noMargin
              className={styles.title}
              size="sm-md"
            >
              {t(keys.common.SUBJECT)}
              {template?.templateOptions?.subject === true ? (
                <span className={styles.red}>*</span>
              ) : null}
            </Text>
            <Form.Control
              onChange={(e) => update({ subject: e.target.value })}
              value={subject || ''}
              placeholder={t(keys.assessments.SUBJECT_PLACEHOLDER)}
            />

            {template?.templateOptions?.location === true ? (
              <>
                <div className={styles.spacer} />
                <Text
                  weight="semiBold"
                  noMargin
                  size="sm-md"
                  className={styles.title}
                >
                  {t(keys.common.LOCATION)}
                  <span className={styles.red}>*</span>
                </Text>
                {!!locations?.length ? (
                  <Dropdown
                    required
                    selected={
                      location ? { label: location, value: location } : null
                    }
                    options={locations}
                    onChange={(obj) => update({ location: obj?.value ?? null })}
                  />
                ) : (
                  <div>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={location}
                      onChange={(e) => update({ location: e.target.value })}
                    />
                    <div className={styles.spacer} />
                  </div>
                )}
              </>
            ) : null}
            <div className={styles.spacer} />
            <Text
              weight="semiBold"
              noMargin
              className={styles.title}
              size="sm-md"
            >
              {t(keys.common.PARTICIPANTS)}
            </Text>
            <UserMultiSelector
              stateUpdateOnly
              setUsers={(participants) => update({ participants })}
              selected={participants}
              type={allWorkspaces ? 'all' : 'users'}
              onUserAdded={(user) => {
                update({ participants: [...participants, user] });
              }}
              onUserRemoved={(user) => {
                update({
                  participants: participants.filter((u) => u.id !== user.id),
                });
              }}
            />
            <br />
            {type?.key !== 'INCIDENT' && (
              <div className={styles.checkboxes}>
                <div className={styles.privateContainer}>
                  <Checkbox
                    checked={
                      privateAssessment ||
                      (!!template && template?.private === true)
                    }
                    disabled={!!template && template?.private === true}
                    onChange={(e) => update({ private: e.target.checked })}
                  />
                  <div>
                    <Text weight="semiBold" noMargin>
                      {t(keys.utilities.PRIVATE)}
                    </Text>
                    <Text size="sm">
                      {t(keys.utilities.PRIVATE_DISCLAIMER)}
                    </Text>
                  </div>
                </div>
                {online && !!allowAnonymous && (
                  <div className={styles.privateContainer}>
                    <Checkbox
                      checked={anonymous}
                      onChange={(e) => update({ anonymous: e.target.checked })}
                    />
                    <div>
                      <Text weight="semiBold" noMargin>
                        {t(keys.utilities.ANONYMOUS)}
                      </Text>
                      <Text size="sm" noMargin>
                        {t(keys.utilities.ANONYMOUS_DISCLAIMER)}
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {lastAssessment?.id && (
          <Text
            noMargin
            weight="semiBold"
            color="accentPrimary"
            hover
            onClick={() => {
              const route = getRoute(
                dataWorkspaceId || selectedWorkspace?.id || workspaceId,
                paths.assessment,
                lastAssessment?.id,
              );
              onHide();
              navigate(route);
            }}
          >
            {t(keys.assessments.LAST_ENTRY_RECORDED)}
            {getLocalTime(
              lastAssessment?.dateCompleted ?? lastAssessment?.dateCreated,
            ).format(isMilitary ? 'MMM D YYYY - HH:mm' : 'MMM D YYYY - h:mma')}
          </Text>
        )}
      </div>
    </Modal>
  );
}
